import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tag, DollarSign, ShoppingBag, MessageSquareText, ShieldCheck, ExternalLink } from "lucide-react";
import Seo, { breadcrumbs, faqSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import QuickTradeEstimator from "../components/QuickTradeEstimator";
import PaymentCalculator from "../components/PaymentCalculator";
import ProtectionStep from "../components/ProtectionStep";
import { SectionHeader, Disclaimer } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
import { siteConfig, disclaimers, fmtMoney, fmtMiles, fees } from "../config";
import { api } from "../lib/api";
import { useFees } from "../lib/feeSettings";
import RebateSelector from "../components/RebateSelector";

const FAQS = [
  { q: "Can I preview a deal online?", a: "Yes — you can choose a new or used vehicle, share your trade and payment direction, add accessories, and send everything to Scott to review." },
  { q: "Is this a final price or payment?", a: "No. Scott’s Deal Desk is for planning and discussion only. Final pricing and payments depend on credit approval, taxes, fees, and dealership confirmation." },
  { q: "Can I include my trade?", a: "Absolutely — Scott uses an internal dealer-grade trade valuation engine to give you a realistic, wholesale-first estimate as a starting point." },
  { q: "Can I add Chevrolet accessories?", a: "Yes — you can browse the official Chevrolet accessory catalog and let Scott know what you want added." },
  { q: "Where do I apply for financing?", a: "Through the official Byers Chevrolet finance application page linked on the Scott’s Deal Desk page." },
  { q: "Will Scott follow up with me?", a: "Yes — Scott personally reviews every Scott’s Deal Desk request and reaches out by your preferred contact method." },
];

export default function BuildYourOwnDeal() {
  const [sp] = useSearchParams();
  const vinParam = (sp.get("vin") || "").trim().toUpperCase();
  const stockParam = (sp.get("stock") || "").trim();
  const titleParam = sp.get("title") || "";
  const statusParam = sp.get("status") || "";
  const urlParam = sp.get("url") || "";
  const focusProtectionSlug = sp.get("protection") || "";

  // Live vehicle resolution. If we have a VIN or stock #, hit the public
  // /api/vehicles endpoint to pull the full record (price, photo, mileage,
  // etc.) so the BYOD page is pre-filled with real data — not the query-
  // string approximation. Falls back gracefully if not found.
  const [vehicle, setVehicle] = useState(null);
  const [vehicleStatus, setVehicleStatus] = useState(vinParam || stockParam ? "loading" : "none");

  useEffect(() => {
    if (!vinParam && !stockParam) return;
    let on = true;
    (async () => {
      try {
        const q = vinParam || stockParam;
        const { data } = await api.get("/vehicles", { params: { search: q, limit: 5 } });
        if (!on) return;
        const items = data?.items || [];
        // Prefer exact VIN/stock match
        const hit = items.find((it) =>
          (vinParam && (it.vin || "").toUpperCase() === vinParam) ||
          (stockParam && String(it.stock_number || "") === stockParam)
        ) || items[0];
        if (hit) {
          setVehicle(hit);
          setVehicleStatus("found");
        } else {
          setVehicleStatus("not_found");
        }
      } catch {
        if (on) setVehicleStatus("not_found");
      }
    })();
    return () => { on = false; };
  }, [vinParam, stockParam]);

  // Resolved values — prefer live record, fall back to URL params.
  const title = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ""}`.trim()
    : titleParam;
  const status = vehicle?.status || statusParam;
  const vin = vehicle?.vin || vinParam;
  const stock = vehicle?.stock_number || stockParam;
  const detailHref = vehicle ? `/${vehicle.status}-vehicles/${vehicle.slug}` : urlParam;
  const advertisedPrice = vehicle?.price || null;
  const effFees = useFees();
  const finalPrice = advertisedPrice ? advertisedPrice + effFees.doc_fee + effFees.title_fee : null;

  const [tradeEstimate, setTradeEstimate] = useState(null);
  const [tradeDetails, setTradeDetails] = useState(null);
  const [showTrade, setShowTrade] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [protectionState, setProtectionState] = useState({ selected: [], declined: [], interactions: [] });
  const [rebateState, setRebateState] = useState(null);
  // v6.5.10 — Deal Preference (optional, no payment-math impact; flows into lead payload)
  const [dealPreference, setDealPreference] = useState(null);

  // v6.5.2 — Sum the prices of any selected Optional Coverages so the
  // PaymentCalculator can include them in Amount Financed.
  const coveragesTotal = (protectionState?.selected || []).reduce(
    (sum, c) => sum + (Number(c.price) > 0 ? Number(c.price) : 0),
    0
  );

  return (
    <main id="main-content">
      <Seo
        title="Scott’s Deal Desk | Preview Your Chevy Deal With Scott Hall"
        description="Use Scott’s Deal Desk to preview pricing, rebates, taxes, trade info, payment goals, and optional protection products before sending your Chevy deal to Scott Hall."
        canonicalPath="/scotts-deal-desk"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Scott’s Deal Desk", path: "/scotts-deal-desk" }]),
          faqSchema(FAQS),
        ]}
      />

      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Scott’s Deal Desk</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">
            Start with the vehicle you want, add your trade information, choose accessories, and take the next step toward financing.
          </p>
        </div>
      </section>

      {/* Pre-fill banner (vehicle resolved from VDP) */}
      {vehicleStatus !== "none" && (
        <section className="ssc-container -mt-6 md:-mt-8 relative z-10">
          {vehicleStatus === "loading" && (
            <div className="rounded-2xl bg-white border border-[var(--ssc-line)] shadow-md p-5 text-sm text-[var(--ssc-ink-2)]" data-testid="byod-prefill-loading">
              Loading vehicle details...
            </div>
          )}
          {vehicleStatus === "found" && vehicle && (
            <div
              className="rounded-2xl bg-white border-2 border-[var(--ssc-blue)] shadow-xl p-4 md:p-5 flex flex-col md:flex-row gap-4 items-stretch"
              data-testid="byod-prefill-card"
            >
              <Link to={detailHref} className="block flex-shrink-0 w-full md:w-[220px]">
                {vehicle.photos?.[0]?.url ? (
                  <img
                    src={vehicle.photos[0].url}
                    alt={title}
                    className="w-full h-[140px] md:h-[150px] object-cover rounded-xl"
                  />
                ) : (
                  <div className="w-full h-[140px] md:h-[150px] bg-[var(--ssc-silver-2)] rounded-xl grid place-items-center text-xs text-[var(--ssc-ink-2)]">
                    Photo coming soon
                  </div>
                )}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`ssc-badge ${vehicle.status === "new" ? "ssc-badge-new" : "ssc-badge-used"}`}>
                    {vehicle.status === "new" ? "New" : "Used"}
                  </span>
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
                    Deal Preview For
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold text-[var(--ssc-navy)] leading-tight mt-1" data-testid="byod-prefill-title">
                  {title}
                </h2>
                <div className="text-xs text-[var(--ssc-ink-2)] mt-1">
                  Stock #{stock} <span className="mx-1.5 text-[var(--ssc-line)]">·</span> VIN {vin}
                  {vehicle.status === "used" && vehicle.mileage != null && (
                    <> <span className="mx-1.5 text-[var(--ssc-line)]">·</span> {fmtMiles(vehicle.mileage)}</>
                  )}
                </div>

                {/* v6.5.1 — Top card shows VEHICLE IDENTITY ONLY. All pricing
                    (MSRP, discount, fees, taxes, rebates, balance due) lives
                    in the lower "Your Deal Numbers" section so customers never
                    see duplicate / conflicting numbers. */}
                <div className="mt-2 text-xs text-[var(--ssc-ink-2)] flex flex-wrap gap-x-3 gap-y-1" data-testid="byod-vehicle-identity">
                  {vehicle.exterior_color && (
                    <span><span className="font-bold text-[var(--ssc-ink)]">Exterior:</span> {vehicle.exterior_color}</span>
                  )}
                  {vehicle.interior_color && (
                    <span><span className="font-bold text-[var(--ssc-ink)]">Interior:</span> {vehicle.interior_color}</span>
                  )}
                  {vehicle.body_style && (
                    <span><span className="font-bold text-[var(--ssc-ink)]">Body:</span> {vehicle.body_style}</span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={detailHref} className="ssc-btn ssc-btn-outline !py-1.5 !text-xs" data-testid="byod-prefill-vdp">
                    <ExternalLink size={13} /> View Vehicle Page
                  </Link>
                  <a
                    href={`${siteConfig.scott.phoneSms}?&body=${encodeURIComponent(`Hi Scott, I'm building a deal on Stock ${stock} — ${title}.`)}`}
                    className="ssc-btn ssc-btn-primary !py-1.5 !text-xs"
                  >
                    <MessageSquareText size={13} /> Text Scott
                  </a>
                </div>
              </div>
            </div>
          )}
          {vehicleStatus === "not_found" && (
            <div
              className="rounded-2xl bg-amber-50 border-2 border-amber-200 shadow-md p-5"
              data-testid="byod-prefill-not-found"
            >
              <div className="text-sm font-extrabold text-amber-900">
                I can still help you preview a deal in Scott’s Deal Desk.
              </div>
              <p className="text-sm text-amber-800 mt-1">
                That vehicle may have just sold or moved — text Scott and I&apos;ll verify availability or help you find the next best match.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary !py-1.5 !text-xs">
                  <MessageSquareText size={13} /> Text Scott
                </a>
                <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-navy !py-1.5 !text-xs">Find Me a Vehicle</Link>
                <Link to="/new-vehicles" className="ssc-btn ssc-btn-outline !py-1.5 !text-xs">Browse New</Link>
                <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline !py-1.5 !text-xs">Browse Used</Link>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="ssc-section">
        <div className="ssc-container grid lg:grid-cols-3 gap-5 lg:gap-8 pb-24 lg:pb-8">
          {/* Steps column */}
          <div className="lg:col-span-2 space-y-4 lg:space-y-6 min-w-0">
            <AboutScottBlurb
              heading="One Place to Send Everything"
              text="Scott’s Deal Desk is a simple way to send Scott the important details in one place. With over 15 years in the car business, Scott can help review the vehicle, trade, payment direction, and accessories so the next step is clearer."
              testId="byod-about-scott"
            />
            <Step n="1" title="Choose Your Vehicle">
              <p className="text-[var(--ssc-ink-2)]">Already have a vehicle in mind? Great — that info comes through from the vehicle page. Otherwise, pick a direction below.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Shop New Vehicles</Link>
                <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Shop Used Vehicles</Link>
                <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-primary">Have Scott Help Me Choose</Link>
              </div>
            </Step>

            <Step n="2" title="Build My Payment">
              <p className="text-[var(--ssc-ink-2)] mb-4">
                Estimate your monthly payment with sales tax, fees, and credit-tier rates baked in. Trade-in info is optional — toggle it on below if you want to include it.
              </p>
              <PaymentCalculator
                initialPrice={advertisedPrice || 28000}
                initialVehicleType={status === "used" || (status || "").toLowerCase().includes("used") ? "used" : "new"}
                onChange={setPaymentSummary}
                testIdPrefix="byod-pmt"
                externalRebatesTotal={vin && (status === "new" || vehicle?.status === "new") ? (rebateState?.total_savings_estimated || 0) : null}
                overrideApr={rebateState?.selected_apr_pct ?? null}
                overrideTerm={rebateState?.selected_apr_term ?? null}
                overrideAprLabel={rebateState?.selected_apr_name ?? null}
                rebatesPanel={
                  vin && (status === "new" || vehicle?.status === "new") ? (
                    <RebateSelector
                      vin={vin}
                      advertisedPrice={advertisedPrice}
                      docFee={effFees.doc_fee}
                      titleFee={effFees.title_fee}
                      dealType={
                        paymentSummary?.financing_type === "lease"
                          ? "lease"
                          : paymentSummary?.financing_type === "cash"
                          ? "cash"
                          : "finance"
                      }
                      paymentSummary={paymentSummary}
                      onChange={setRebateState}
                    />
                  ) : null
                }
                externalCoveragesTotal={coveragesTotal}
                coveragesAnchorId="byod-step-coverages"
              />
            </Step>

            <Step n="3" title="Estimate My Trade (optional)">
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-[var(--ssc-line)] bg-white" data-testid="byod-trade-toggle-wrap">
                <input
                  type="checkbox"
                  checked={showTrade}
                  onChange={(e) => setShowTrade(e.target.checked)}
                  className="w-5 h-5"
                  data-testid="byod-trade-toggle"
                />
                <span className="font-semibold text-[var(--ssc-ink)]">
                  I have a vehicle to trade in — show me the trade estimator
                </span>
              </label>
              {showTrade && (
                <div className="mt-4" data-testid="byod-trade-panel">
                  <QuickTradeEstimator testIdPrefix="byod-quick" onValue={(r) => { setTradeEstimate(r.dealer_trade_high); setTradeDetails(r); }} />
                </div>
              )}
            </Step>

            <Step n="4" title="Add Coverages" icon={<ShieldCheck size={20} />} anchorId="byod-step-coverages" collapsibleOnMobile mobileSummary="Optional protection products (tap to expand)">
              <ProtectionStep
                initialFocusSlug={focusProtectionSlug}
                onChange={setProtectionState}
                recommendationContext={{
                  vehicleTitle: title,
                  vehicleStatus: status,
                  paymentTerm: paymentSummary?.term_months || paymentSummary?.term || null,
                  financing: !!(paymentSummary?.financing_type !== "cash" && paymentSummary?.term_months),
                  vehicleInterest: title,
                }}
              />
            </Step>

            <Step n="5" title="Apply for Financing">
              <p className="text-[var(--ssc-ink-2)]">When you're ready to lock it in, apply with Byers Chevrolet.</p>
              <a href={siteConfig.links.financeApply} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-navy mt-3" data-testid="byod-apply-finance">
                <DollarSign size={16} /> Apply for Financing
              </a>
            </Step>

            <Step n="6" title="Add Accessories" collapsibleOnMobile mobileSummary="Add Chevrolet accessories later (tap to expand)">
              <p className="text-[var(--ssc-ink-2)]">Browse available Chevrolet accessories and let Scott know what you want added to your deal.</p>
              <a href={siteConfig.links.accessories} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-primary mt-3" data-testid="byod-shop-accessories">
                <ShoppingBag size={16} /> Shop Chevrolet Accessories
              </a>
            </Step>

            <Step n="7" title="What matters most for this deal? (optional)" anchorId="byod-step-preference">
              <DealPreferencePicker value={dealPreference} onChange={setDealPreference} />
            </Step>

            <Step n="8" title="Save This Deal" collapsibleOnMobile mobileSummary="Save or send your deal to Scott (tap to expand)">
              <SavedDealSummary
                paymentSummary={paymentSummary}
                title={title}
                stock={stock}
                vin={vin}
                tradeEstimate={tradeEstimate}
                protectionState={protectionState}
                rebateState={rebateState}
                dealPreference={dealPreference}
              />
            </Step>

            <Step n="9" title="Not Sure? Message Scott">
              <p className="text-[var(--ssc-ink-2)]">Got a question on numbers, the vehicle, or your trade? Drop Scott a quick note and he'll get back to you fast.</p>
              {(rebateState?.selected?.length || 0) > 0 && !rebateState?.acknowledged && (
                <div
                  className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
                  data-testid="byod-ack-required"
                >
                  Please check the acknowledgment in the Rebates step above before sending this deal — selected GM offers are estimates and need dealer confirmation.
                </div>
              )}
              {(!(rebateState?.selected?.length) || rebateState?.acknowledged) && (
              <LeadForm
                leadType="build_your_own_deal"
                leadSource="Scott’s Deal Desk Page"
                testIdPrefix="byod-lead"
                fields={[
                  { name: "name", label: "Name", required: true, autoComplete: "name" },
                  { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
                  { name: "email", label: "Email", type: "email", autoComplete: "email" },
                  { name: "preferred_contact", label: "Preferred Contact", type: "select", options: ["Text", "Call", "Email"] },
                  { name: "message", label: "What are you not sure about?", type: "textarea", full: true, placeholder: "Examples: 'Can you double-check my trade?' · 'How firm is the price?' · 'Is this rate realistic for my credit?'" },
                ]}
                hidden={{
                  vehicle_title: title,
                  vin,
                  stock_number: stock,
                  new_or_used: status,
                  year: vehicle?.year || null,
                  make: vehicle?.make || null,
                  model: vehicle?.model || null,
                  trim: vehicle?.trim || null,
                  advertised_price: advertisedPrice,
                  doc_fee: effFees.doc_fee,
                  title_fee: effFees.title_fee,
                  final_price_before_taxes: finalPrice,
                  vdp_source: detailHref || null,
                  vehicle_mileage: vehicle?.mileage ?? null,
                  cta_clicked: "BYOD Not Sure",
                  // v6.5.10 — Complete itemized trade context for Scott
                  trade_included: !!paymentSummary?.include_trade,
                  trade_estimate: tradeEstimate || null,
                  trade_details: tradeDetails || null,
                  trade_value: paymentSummary?.trade_value ?? null,
                  trade_payoff: paymentSummary?.trade_payoff ?? null,
                  trade_equity: paymentSummary?.trade_equity ?? null,
                  trade_negative_equity: paymentSummary?.trade_negative_equity ?? null,
                  trade_year: paymentSummary?.trade_year || null,
                  trade_make: paymentSummary?.trade_make || null,
                  trade_model: paymentSummary?.trade_model || null,
                  trade_mileage: paymentSummary?.trade_mileage || null,
                  // v6.5.10 — Itemized deal math for Scott
                  deal_doc_fee: paymentSummary?.doc_fee ?? null,
                  deal_state_fee: paymentSummary?.state_fee ?? null,
                  deal_title_fee: paymentSummary?.title_fee ?? null,
                  deal_temp_tag_selected: paymentSummary?.temp_tag_selected ?? false,
                  deal_temp_tag_fee: paymentSummary?.temp_tag_fee ?? 0,
                  deal_county: paymentSummary?.county || null,
                  deal_county_tax_rate: paymentSummary?.tax_rate ?? null,
                  deal_sales_tax: paymentSummary?.tax ?? null,
                  deal_taxable_amount: paymentSummary?.taxable_amount ?? null,
                  deal_amount_financed: paymentSummary?.amount_financed ?? null,
                  deal_monthly: paymentSummary?.monthly ?? null,
                  deal_term_selected: paymentSummary?.term ?? null,
                  deal_effective_term: paymentSummary?.effective_term ?? null,
                  deal_credit_tier: paymentSummary?.credit_tier || null,
                  deal_using_override_apr: paymentSummary?.using_override_apr ?? false,
                  deal_override_apr: paymentSummary?.override_apr ?? null,
                  deal_override_apr_label: paymentSummary?.override_apr_label || null,
                  deal_down_payment: paymentSummary?.down ?? null,
                  // v6.5.10 — Deal preference (intent)
                  deal_preference: dealPreference?.value || null,
                  deal_preference_label: dealPreference?.label || null,
                  payment_summary: paymentSummary || null,
                  byod_vehicle_url: detailHref || null,
                  protection_selected: protectionState.selected,
                  protection_declined: protectionState.declined,
                  protection_interactions: protectionState.interactions,
                  protection_total: protectionState.selected.reduce((sum, p) => sum + (p.price || 0), 0),
                  // GM Rebates (Phase B+)
                  selected_rebates: rebateState?.selected || [],
                  total_rebate_savings_estimated: rebateState?.total_savings_estimated || 0,
                  estimated_price_if_qualified: rebateState?.estimated_price_if_qualified || null,
                  // Payment-side estimate: linear scale of base monthly by (financed - rebates)/financed.
                  estimated_payment_if_qualified:
                    (paymentSummary?.monthly_payment ?? paymentSummary?.payment ?? null) &&
                    paymentSummary?.amount_financed > 0 &&
                    rebateState?.total_savings_estimated > 0
                      ? Math.round(
                          (paymentSummary.monthly_payment ?? paymentSummary.payment) *
                            Math.max(
                              0,
                              (paymentSummary.amount_financed - (rebateState.total_savings_estimated || 0)) /
                                paymentSummary.amount_financed
                            )
                        )
                      : null,
                  rebate_acknowledged_at: rebateState?.acknowledged ? new Date().toISOString() : null,
                }}
                submitLabel="Message Scott"
                successMessage="Thanks — Scott will reach out shortly."
              />
              )}
              <div className="mt-3"><Disclaimer text={disclaimers.byod} /></div>
            </Step>
          </div>

          {/* Side rail — collapsed accordions on mobile, auto-opened on lg+ */}
          <aside className="space-y-4 lg:space-y-5" ref={(el) => {
            if (!el) return;
            const apply = () => {
              const wide = window.matchMedia("(min-width: 1024px)").matches;
              el.querySelectorAll("details[data-byod-rail]").forEach((d) => {
                if (wide) d.setAttribute("open", "");
                else d.removeAttribute("open");
              });
            };
            apply();
            window.addEventListener("resize", apply);
          }}>
            <details data-byod-rail className="ssc-card-dark group">
              <summary className="cursor-pointer list-none flex items-center justify-between lg:cursor-default">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[var(--ssc-silver)]">Need help?</div>
                  <div className="font-extrabold text-lg sm:text-xl mt-0.5">Text Scott</div>
                </div>
                <span className="lg:hidden text-[var(--ssc-silver)] text-2xl leading-none group-open:rotate-45 transition-transform" aria-hidden>+</span>
              </summary>
              <p className="text-[var(--ssc-silver)] text-sm mt-2 lg:mt-1">Quick questions get quick answers.</p>
              <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary w-full mt-3">
                <MessageSquareText size={16} /> Text Scott
              </a>
            </details>
            <details data-byod-rail className="ssc-card group">
              <summary className="cursor-pointer list-none flex items-center justify-between lg:cursor-default">
                <h3 className="font-extrabold text-[var(--ssc-navy)]">FAQs</h3>
                <span className="lg:hidden text-[var(--ssc-ink-2)] text-2xl leading-none group-open:rotate-45 transition-transform" aria-hidden>+</span>
              </summary>
              <ul className="mt-3 space-y-3 text-sm">
                {FAQS.map((f, i) => (
                  <li key={i}>
                    <div className="font-semibold text-[var(--ssc-navy)]">{f.q}</div>
                    <div className="text-[var(--ssc-ink-2)]">{f.a}</div>
                  </li>
                ))}
              </ul>
            </details>
          </aside>
        </div>
      </section>
    </main>
  );
}

// v6.5.12 — Step component supports optional mobile collapse for low-priority sections.
// When collapsibleOnMobile is set, the step renders as a <details> element:
//   • collapsed by default on screens < lg (1024px)
//   • auto-opened on lg+ via the matchMedia ref callback below
// High-priority steps (Vehicle, Payment, Trade, Offers, Preference, Submit) leave
// this prop OFF so they stay fully open at all widths.
const openOnLgRef = (el) => {
  if (!el) return;
  const apply = () => {
    const wide = window.matchMedia("(min-width: 1024px)").matches;
    if (wide) el.setAttribute("open", "");
    else el.removeAttribute("open");
  };
  apply();
  window.addEventListener("resize", apply);
};

const Step = ({ n, title, children, anchorId, collapsibleOnMobile, mobileSummary }) => {
  if (collapsibleOnMobile) {
    return (
      <details ref={openOnLgRef} className="ssc-card p-4 sm:p-5 group" id={anchorId || undefined}>
        <summary className="cursor-pointer list-none flex items-center justify-between gap-2.5 sm:gap-3 lg:cursor-default">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[var(--ssc-navy)] text-white grid place-items-center font-extrabold text-sm sm:text-base shrink-0">{n}</div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-extrabold text-[var(--ssc-navy)] leading-tight">{title}</h2>
              {mobileSummary && (
                <p className="text-xs text-[var(--ssc-ink-2)] mt-0.5 lg:hidden group-open:hidden">{mobileSummary}</p>
              )}
            </div>
          </div>
          <span className="lg:hidden text-[var(--ssc-ink-2)] text-2xl leading-none group-open:rotate-45 transition-transform shrink-0" aria-hidden>+</span>
        </summary>
        <div className="mt-3">{children}</div>
      </details>
    );
  }
  return (
    <div className="ssc-card p-4 sm:p-5" id={anchorId || undefined}>
      <div className="flex items-center gap-2.5 sm:gap-3">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[var(--ssc-navy)] text-white grid place-items-center font-extrabold text-sm sm:text-base shrink-0">{n}</div>
        <h2 className="text-lg sm:text-xl font-extrabold text-[var(--ssc-navy)] leading-tight">{title}</h2>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
};

// v6.5.10 — Optional Deal Preference picker. No payment-math impact;
// captured for the lead so Scott understands customer intent.
const DEAL_PREFERENCE_OPTIONS = [
  { value: "lowest_payment", label: "Lowest Monthly Payment", helper: "Help me keep the payment as low as possible." },
  { value: "shortest_term", label: "Shortest Term", helper: "Help me pay it off faster." },
  { value: "lowest_total_cost", label: "Lowest Total Cost", helper: "Help me pay the least overall." },
  { value: "least_down", label: "Least Money Down", helper: "Help me keep cash out of pocket lower." },
];

function DealPreferencePicker({ value, onChange }) {
  return (
    <div data-testid="byod-deal-preference">
      <p className="text-sm text-[var(--ssc-ink-2)] mb-3">
        This helps Scott understand what kind of deal you're trying to build. You can still change the numbers anytime.
      </p>
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
        {DEAL_PREFERENCE_OPTIONS.map((opt) => {
          const selected = value?.value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(selected ? null : opt)}
              className={`text-left rounded-xl sm:rounded-2xl border-2 p-3 sm:p-4 transition-colors ${selected ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
              data-testid={`byod-deal-pref-${opt.value}`}
              aria-pressed={selected}
            >
              <div className="flex items-start justify-between gap-1.5">
                <div className="font-extrabold text-[var(--ssc-navy)] text-sm sm:text-base leading-tight">{opt.label}</div>
                <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 shrink-0 mt-0.5 ${selected ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]" : "border-[var(--ssc-line)]"}`}>
                  {selected && <div className="w-full h-full rounded-full bg-[var(--ssc-blue)] ring-2 ring-white ring-inset" />}
                </div>
              </div>
              <p className="text-[11px] sm:text-xs text-[var(--ssc-ink-2)] mt-1.5 leading-snug">{opt.helper}</p>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => onChange(null)}
        className="mt-3 text-xs font-bold text-[var(--ssc-ink-2)] underline"
        data-testid="byod-deal-pref-later"
      >
        I'll decide later
      </button>
    </div>
  );
}



function PriceCell({ label, value, accent, bold, strike }) {
  return (
    <div className={`rounded-lg border p-2 ${bold ? "border-[var(--ssc-navy)] bg-[var(--ssc-navy)] text-white" : "border-[var(--ssc-line)] bg-[var(--ssc-paper)]"}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wide ${bold ? "text-white/70" : "text-[var(--ssc-ink-2)]"}`}>{label}</div>
      <div className={`tabular-nums font-extrabold mt-0.5 ${strike ? "line-through text-[var(--ssc-ink-2)]" : bold ? "text-white" : accent ? "text-[var(--ssc-navy)]" : "text-[var(--ssc-ink)]"}`}>{value}</div>
    </div>
  );
}

function SavedDealSummary({ paymentSummary, title, stock, vin, tradeEstimate, protectionState, rebateState, dealPreference }) {
  if (!paymentSummary) {
    return (
      <p className="text-[var(--ssc-ink-2)] text-sm" data-testid="byod-saved-empty">
        Build your payment in Step 2 first — your saved summary will show up here so you can share it with Scott in one tap.
      </p>
    );
  }
  const fmt = (n) => (n === null || n === undefined || isNaN(n)) ? "—" : `$${Math.round(n).toLocaleString()}`;
  const monthly = paymentSummary.monthly_payment ?? paymentSummary.payment ?? null;
  const term = paymentSummary.term_months ?? paymentSummary.term ?? null;
  const amountFinanced = paymentSummary.amount_financed ?? null;
  const protectionTotal = (protectionState?.selected || []).reduce((s, p) => s + (p.price || 0), 0);

  // Estimated Payment If Qualified — applies only selected compatible
  // rebates to the financed amount, never to advertised price.
  // Strict rules:
  //   - Only selected offers (rebate engine already filters by compat).
  //   - "Already included in price" offers are NOT in selectable list, so they're
  //     automatically excluded here.
  //   - Special financing is in a separate list (financing[]), NOT in selected_rebates.
  //   - Estimate-only — labeled clearly as such.
  const rebateSavings = rebateState?.total_savings_estimated || 0;
  const hasRebates = rebateSavings > 0 && monthly && amountFinanced && amountFinanced > 0;
  // Payment scales linearly with principal at fixed APR + term.
  const ifQualifiedMonthly = hasRebates
    ? Math.max(0, monthly * Math.max(0, (amountFinanced - rebateSavings) / amountFinanced))
    : null;

  const sms = `Hey Scott — I just built a deal on the ${title || "Chevy"}${stock ? ` (stock ${stock})` : ""}: ~${fmt(monthly)}/mo${term ? ` for ${term} mo` : ""}${tradeEstimate ? ` with my trade at ~${fmt(tradeEstimate)}` : ""}${ifQualifiedMonthly ? ` — could be ~${fmt(ifQualifiedMonthly)}/mo with selected rebates if I qualify` : ""}. Can you confirm?`;
  return (
    <div data-testid="byod-saved-summary">
      <div className="rounded-2xl border-2 border-[var(--ssc-blue)] bg-blue-50 p-4">
        <div className="text-xs uppercase tracking-wider text-[var(--ssc-blue)] font-bold">Estimated Monthly Payment</div>
        <div className="text-3xl font-extrabold text-[var(--ssc-navy)] mt-1" data-testid="byod-monthly-base">{fmt(monthly)}/mo{term ? <span className="text-sm font-normal text-[var(--ssc-ink-2)]"> for {term} mo</span> : null}</div>

        {ifQualifiedMonthly !== null && (
          <div className="mt-3 pt-3 border-t border-blue-200" data-testid="byod-monthly-if-qualified-block">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
              After {rebateState.selected.length} compatible selected rebate{rebateState.selected.length === 1 ? "" : "s"}
            </div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <div className="text-2xl font-extrabold text-emerald-700" data-testid="byod-monthly-if-qualified">
                {fmt(ifQualifiedMonthly)}/mo
              </div>
              <div className="text-xs text-[var(--ssc-ink-2)]">Estimated Monthly Payment If Qualified</div>
            </div>
            <p className="text-[10px] text-[var(--ssc-ink-2)] mt-1 leading-relaxed" data-testid="byod-monthly-if-qualified-note">
              Estimate only. Final payment depends on approved credit, term, rate, taxes, fees, selected offers, and lender approval.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
          <SumLine label="Vehicle price" value={fmt(paymentSummary.vehicle_price)} />
          <SumLine label="Trade-in" value={paymentSummary.trade ? `− ${fmt(Math.abs(paymentSummary.trade))}` : "—"} />
          <SumLine label="Rebates" value={paymentSummary.rebates ? `− ${fmt(Math.abs(paymentSummary.rebates))}` : "—"} />
          <SumLine label="Down payment" value={paymentSummary.down ? `− ${fmt(Math.abs(paymentSummary.down))}` : "—"} />
          {paymentSummary.tax !== undefined && <SumLine label={`Sales tax${paymentSummary.tax_county ? ` (${paymentSummary.tax_county})` : ""}`} value={`+ ${fmt(paymentSummary.tax)}`} />}
          {paymentSummary.doc_fee !== undefined && <SumLine label="Doc fee" value={`+ ${fmt(paymentSummary.doc_fee)}`} />}
          {paymentSummary.state_fee !== undefined && <SumLine label="State fee" value={`+ ${fmt(paymentSummary.state_fee)}`} />}
          {paymentSummary.title_fee !== undefined && <SumLine label="Title fee" value={`+ ${fmt(paymentSummary.title_fee)}`} />}
          {paymentSummary.temp_tag_selected && <SumLine label="Temporary tag" value={`+ ${fmt(paymentSummary.temp_tag_fee)}`} />}
          {protectionTotal > 0 && <SumLine label="Optional coverages" value={`+ ${fmt(protectionTotal)}`} />}
          {paymentSummary.amount_financed !== undefined && <SumLine bold label="Amount financed" value={fmt(paymentSummary.amount_financed)} />}
        </div>

        {/* v6.5.10 — Show APR override + trade equity + deal preference summary */}
        {(paymentSummary.using_override_apr || (paymentSummary.trade_equity && paymentSummary.trade_equity !== 0) || dealPreference) && (
          <div className="mt-3 pt-3 border-t border-blue-200 text-xs space-y-1.5" data-testid="byod-saved-extras">
            {paymentSummary.using_override_apr && (
              <div className="flex items-center gap-2 text-emerald-700 font-bold">
                <span className="text-[10px] uppercase tracking-widest">Special APR Selected:</span>
                <span>{paymentSummary.override_apr_label || `${paymentSummary.override_apr}% APR for ${paymentSummary.effective_term} mo`}</span>
              </div>
            )}
            {paymentSummary.include_trade && (paymentSummary.trade_year || paymentSummary.trade_make || paymentSummary.trade_model) && (
              <div className="text-[var(--ssc-ink-2)]">
                <span className="font-bold text-[var(--ssc-navy)]">Trade:</span>{" "}
                {[paymentSummary.trade_year, paymentSummary.trade_make, paymentSummary.trade_model].filter(Boolean).join(" ")}
                {paymentSummary.trade_mileage ? ` · ${paymentSummary.trade_mileage} mi` : ""}
              </div>
            )}
            {paymentSummary.include_trade && paymentSummary.trade_equity !== 0 && (
              paymentSummary.trade_equity > 0 ? (
                <div className="text-emerald-700 font-bold">Estimated trade equity: {fmt(paymentSummary.trade_equity)}</div>
              ) : (
                <div className="text-amber-700 font-bold">Estimated negative equity: {fmt(paymentSummary.trade_negative_equity)} (added to financed)</div>
              )
            )}
            {dealPreference && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-[var(--ssc-blue)] font-bold">Your priority:</span>
                <span className="font-bold text-[var(--ssc-navy)]">{dealPreference.label}</span>
              </div>
            )}
          </div>
        )}

        {paymentSummary.saved_code && (
          <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg" data-testid="byod-saved-code">
            <div className="text-xs text-[var(--ssc-ink-2)]">Saved as</div>
            <div className="font-mono font-bold text-lg text-[var(--ssc-navy)]">{paymentSummary.saved_code}</div>
            <div className="text-xs text-[var(--ssc-ink-2)] mt-1">Share this code with Scott to pull it up instantly.</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <a href={`sms:6145358673&body=${encodeURIComponent(sms)}`} className="ssc-btn ssc-btn-primary" data-testid="byod-saved-sms">Text This Deal to Scott</a>
          <a href={`mailto:askscott@scottsellschevys.com?subject=${encodeURIComponent("My Scott’s Deal Desk — please review")}&body=${encodeURIComponent(sms)}`} className="ssc-btn ssc-btn-secondary" data-testid="byod-saved-email">Email This to Scott</a>
        </div>

        <p className="text-[11px] text-[var(--ssc-ink-2)] mt-3 leading-relaxed">
          Not an actual rate quote. Monthly payment is a guesstimate based on national-average rates for the credit tier you selected.
          Real APR depends on full credit underwriting, lender, term, and the vehicle — your offer could be higher or lower. Maximum rate used in this calculator is 24.99%.
          Text Scott at 614-535-8673 for a real number from his lenders.
        </p>
      </div>
    </div>
  );
}

function SumLine({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between border-b border-blue-100 py-1 ${bold ? "font-bold text-[var(--ssc-navy)]" : "text-[var(--ssc-ink-2)]"}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
